import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import useIsMounted from "../../../../../vodea/utilities/hooks/useIsMounted";
import { Helmet } from "react-helmet-async";
import VuiBreadcrumb from "../../../../../vodea/@vodea-ui/components/VuiBreadcrumb";
import BrandRoleRepository from "../../../../../repositories/Brand/RoleRepository";
import VendorRoleRepository from "../../../../../repositories/Vendor/RoleRepository";
import {
  formatFormData,
  formatSetValueForm,
} from "../../../../../vodea/utilities/helpers/form";
import { AxiosError, AxiosResponse } from "axios";
import { Controller, useForm } from "react-hook-form";
import {
  InformationBaseModel,
  InformationInputs,
  informationSchema,
} from "./validation";
import {
  mapHookErrors,
  showToast,
} from "../../../../../vodea/utilities/helpers/global";
import { useState } from "@hookstate/core";
import { yupResolver } from "@hookform/resolvers/yup";
import clsx from "clsx";
import VuiActionForm from "../../../../../vodea/@vodea-ui/components/Forms/VuiActionForm";
import _ from "lodash";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../stores";
import VuiSelect from "../../../../../vodea/@vodea-ui/components/Forms/VuiSelect";
import useRoleService from "../../../../../vodea/utilities/hooks/useRoleService";
import RoleRepository from "../../../../../repositories/RoleRepository";

const SettingRoleForm: React.FC<any> = () => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const { id } = useParams();
  const { brand } = useSelector((state: RootState) => state.brand);
  const { vendor } = useSelector((state: RootState) => state.vendor);
  const { isAdministrator, isKeyAccount, isVendor, isBrand } = useRoleService();

  const title = `${id ? t("general.edit") : t("general.create")} ${t(
    "settings.role.title"
  )}`;

  useEffect(() => {
    if (id) {
      getData();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getData = () => {
    (isVendor()
      ? VendorRoleRepository.show(vendor.id, id)
      : isBrand()
      ? BrandRoleRepository.show(brand.id, id)
      : RoleRepository.show(id)
    )
      .then((response: AxiosResponse) => {
        const data = response.data.data;

        const formattedData = formatSetValueForm(InformationBaseModel, data);

        _.forEach(formattedData, (value, name: any) => {
          informationSetValue(name, value);
        });
      })
      .catch((e: AxiosError) => {
        showToast(e.message, "error");
      });
  };

  const informationLoading = useState(false);
  const {
    register: informationRegister,
    handleSubmit: informationHandleSubmit,
    errors: informationErrors,
    setError: informationSetError,
    setValue: informationSetValue,
    control: informationControl,
  } = useForm<InformationInputs>({
    resolver: yupResolver(informationSchema),
  });

  const onInformationSubmit = informationHandleSubmit((data) => {
    informationLoading.set(true);

    const formData = formatFormData(data);

    (id
      ? isVendor()
        ? VendorRoleRepository.update(vendor.id, id, formData)
        : isBrand()
        ? BrandRoleRepository.update(brand.id, id, formData)
        : RoleRepository.update(id, formData)
      : isVendor()
      ? VendorRoleRepository.create(vendor.id, formData)
      : isBrand()
      ? BrandRoleRepository.create(brand.id, formData)
      : RoleRepository.create(formData)
    )
      .then((response: AxiosResponse) => {
        showToast(t("response.successUpdateData"), "success");
        informationLoading.set(false);

        navigate("/settings/role");
      })
      .catch((e: AxiosError) => {
        if (isMounted.current && e?.response?.data?.errors) {
          const errors = mapHookErrors(e.response.data.errors);
          Object.keys(errors).forEach((key: any) => {
            informationSetError(key, errors[key]);
          });

          showToast(e.response.data.message, "error");
        }

        informationLoading.set(false);
      });
  });

  const breadcrumbList = [
    {
      label: t("settings.title"),
      link: "/settings",
    },
    {
      label: t("settings.role.title"),
      link: "/settings/role",
    },
    {
      label: title,
      link: `/settings/role/${id ? id : "create"}`,
    },
  ];

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <VuiBreadcrumb list={breadcrumbList} />

      <div className={"page-header-component"}>
        <h3 className={"title"}>{title}</h3>
      </div>

      <form className={"form-wrapper"} onSubmit={onInformationSubmit}>
        <div className={"default-page-layout"}>
          <div className={"information-section"}>
            <div className={"card-paper"}>
              <div className={"card-header"}>
                <h6 className={"card-header-title"}>
                  {t("general.information")}
                </h6>
              </div>
              <div className={"card-content"}>
                <div className="row">
                  <div className="col-md-6">
                    <div
                      className={clsx({
                        "form-group": true,
                        error: informationErrors.name,
                      })}
                    >
                      <label className={"form-label"}>
                        {t("form.name.label")}
                      </label>
                      <input
                        type="text"
                        name="name"
                        className="form-control"
                        ref={informationRegister}
                      />
                      <label className={"label-help"}>
                        {informationErrors.name?.message}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <VuiActionForm
          loading={informationLoading.get()}
          cancelLink={"/settings/role"}
        />
      </form>
    </>
  );
};

export default SettingRoleForm;
